/**
 * contexts/SimulatorContext.jsx
 * Estado central del simulador de citas.
 * Todo el estado es in-memory (sin escritura en DynamoDB).
 */

import { esModoAcademia, entrarModoAcademiaSim } from "../simulator/pages/autoescuela/modoAcademiaSim";
import React, { createContext, useContext, useReducer, useRef } from "react";
import { linkStore } from "../simulator/store";
import { resetSimDemoSession, aplicarLeadComoAlumno } from "../simulator/pages/autoescuela/simAlumnosStore";
import { resetAgendaLlena } from "../simulator/api/agendaLlenaApi";
import { aplicarNombreLeadNegocio } from "../simulator/pages/autoescuela/autoescuelaBillingStore";
import { resetAutoescuelaChats } from "../simulator/pages/autoescuela/chats/autoescuelaChatsMock";
import activityTracker from "../lib/activityTracker";
import { 
  getWorkersForSector, 
  getContactsForSector, 
  getServicesForSector,
  generateMockCalendarEvents
} from "../simulator/mockData";
import { trabajadoresParaNumero, idDeTrabajador } from "../simulator/trabajadoresDemo";

/* ─── Estado inicial ──────────────────────────────────────── */

const initialState = {
  isActive: false,
  sector: null,
  sectorLabel: "",
  services: [],
  workers: [],
  contacts: [],
  appointments: [],
  simNotifications: [],
  campaigns: [],
  currentPage: "calendario",
  selectedCampaignId: null,
  selectedAlumnoId: null,
  navOptions: null,
  // Opciones con las que se llegó a la página actual. A diferencia de `navOptions`, NO las borra
  // `clearNavOptions()`: de aquí lee la flecha «Volver a …» (`backTo`) y el `restore` del origen
  // (simulator/navigation/simBackLink.js). `navSeq` cuenta las navegaciones (reponer el scroll).
  navArrival: null,
  navSeq: 0,
  logoUrl: null,
  contactId: null,
  contactName: null,
  contactPhone: null,
  personaContactoNombre: null,
  personaContactoPhone: null,
  returnLead: null, // { contactId, initialData } — set when entering from LeadEditModal
};

/* ─── Reducer ─────────────────────────────────────────────── */

function simulatorReducer(state, action) {
  switch (action.type) {
    case "ACTIVATE":
      return { ...initialState, isActive: true, currentPage: "calendario", returnLead: state.returnLead };

    case "DEACTIVATE":
      return { ...initialState, returnLead: state.returnLead };

    case "SET_RETURN_LEAD":
      return { ...state, returnLead: action.payload };

    case "CLEAR_RETURN_LEAD":
      return { ...state, returnLead: null };

    case "SET_SECTOR": {
      const { sector, sectorLabel, services, workers: providedWorkers, logoUrl, contactId, contactName, contactPhone, personaContactoNombre, personaContactoPhone } = action.payload;
      const contacts = getContactsForSector(sector);
      // Use real workers from backend when available; fall back to mock workers.
      // Cap at 2 workers max — the simulator demo is clearer with fewer workers.
      const rawWorkers =
        Array.isArray(providedWorkers) && providedWorkers.length > 0
          ? providedWorkers
          : getWorkersForSector(sector);
      const resolvedWorkers = rawWorkers.slice(0, 2);
      // Use real/sector-config services when available; fall back to mock services
      const resolvedServices =
        Array.isArray(services) && services.length > 0
          ? services
          : getServicesForSector(sector);

      // Generar citas mock para el calendario basadas en el sector, usando los recursos reales si los hay
      // food-orders and autoescuela don't use appointment calendar
      const mockAppointments =
        sector === "comida_para_llevar" || sector === "distribucion" || sector === "autoescuela" ||
        sector === "bares_deportes" || sector === "bares_tematicos"
          ? []
          : generateMockCalendarEvents(null, null, sector, resolvedWorkers, resolvedServices);

      // Notificaciones pre-sembradas para la demo (solo sectores con calendario
      // de citas): 2 entradas no leídas derivadas de las citas mock.
      // generateMockCalendarEvents ya compone un `title` legible ("HH:mm-HH:mm:
      // servicio - profesional") y usa `client_name` (no `customer_name`); se
      // prioriza ese título ya compuesto y se cae a service_name/client_name
      // solo si faltara.
      const simNotifications = mockAppointments.slice(0, 2).map((apt, i) => ({
        id: `seed-${apt.event_id || apt.id || i}`,
        event_id: apt.event_id || apt.id || `seed-${i}`,
        title:
          apt.title ||
          [apt.service_name, apt.client_name || apt.customer_name]
            .filter(Boolean)
            .join(" - ") ||
          "",
        start: apt.start || null,
        received_at: Date.now() - (i + 1) * 42 * 60000,
        read: false,
      }));

      return {
        ...state,
        sector,
        sectorLabel,
        services: resolvedServices,
        workers: resolvedWorkers,
        contacts,
        appointments: mockAppointments,
        simNotifications,
        currentPage:
          sector === "comida_para_llevar" ? "pedidos" :
          sector === "distribucion" ? "pedidos" :
          sector === "restaurantes" ? "pedidos" :
          sector === "autoescuela" ? "autoescuelaDashboard" :
          sector === "marketing" ? "marketingCampanas" :
          sector === "bares_deportes" || sector === "bares_tematicos" ? "barListas" :
          "calendario",
        navArrival: null,
        logoUrl: logoUrl || null,
        contactId: contactId || null,
        contactName: contactName || null,
        contactPhone: contactPhone || null,
        personaContactoNombre: personaContactoNombre || null,
        personaContactoPhone: personaContactoPhone || null,
      };
    }

    case "ADD_APPOINTMENT": {
      const apt = action.payload || {};
      const next = {
        ...state,
        appointments: [...state.appointments, apt],
      };
      // Espejo del centro de notificaciones: solo reservas de cliente
      // (origin_kind "booking"), igual que en la app real (las citas creadas
      // por el admin desde el CRM no generan aviso).
      if (apt.origin_kind === "booking") {
        const aptId = apt.event_id || apt.id;
        next.simNotifications = [
          {
            id: String(aptId),
            event_id: String(aptId),
            title:
              [apt.service_name, apt.customer_name].filter(Boolean).join(" - ") ||
              apt.title ||
              "",
            start:
              apt.start ||
              (apt.date && apt.start_time
                ? `${apt.date}T${apt.start_time}:00`
                : null),
            received_at: Date.now(),
            read: false,
          },
          ...state.simNotifications,
        ].slice(0, 50);
      }
      return next;
    }

    case "UPDATE_APPOINTMENT":
      return {
        ...state,
        appointments: state.appointments.map((apt) =>
          apt.event_id === action.payload.id || apt.id === action.payload.id
            ? { ...apt, ...action.payload.changes }
            : apt,
        ),
      };

    case "DELETE_APPOINTMENT":
      return {
        ...state,
        appointments: state.appointments.filter(
          (apt) => apt.event_id !== action.payload && apt.id !== action.payload,
        ),
      };

    case "MARK_SIM_NOTIFICATIONS_READ":
      return {
        ...state,
        simNotifications: state.simNotifications.map((n) =>
          n.read ? n : { ...n, read: true },
        ),
      };

    case "SET_SERVICES":
      return { ...state, services: action.payload };

    case "UPDATE_WORKER": {
      const { user_id, ...changes } = action.payload || {};
      if (!user_id) return state;
      return {
        ...state,
        workers: (state.workers || []).map((w) => {
          const wid = w.worker_id || w.user_id || w.id;
          if (wid !== user_id) return w;
          return { ...w, ...changes };
        }),
      };
    }

    // Alta de trabajador creada por el usuario en el simulador. Solo en memoria:
    // el estado se limpia al DEACTIVATE (salir del simulador).
    case "ADD_WORKER": {
      const w = action.payload || {};
      const uid = w.user_id || w.worker_id || w.id;
      if (!uid) return state;
      // Evitar duplicados si ya existe ese trabajador.
      if (
        (state.workers || []).some(
          (x) => (x.worker_id || x.user_id || x.id) === uid,
        )
      ) {
        return state;
      }
      return {
        ...state,
        workers: [
          ...(state.workers || []),
          { ...w, user_id: uid, worker_id: uid },
        ],
      };
    }

    // «− N trabajadores +» del calendario (trabajadoresDemo.js). Los que entran por primera vez reciben
    // sus citas de ejemplo; las de los que salen se quedan guardadas y dejan de verse (axiosClient).
    case "SET_NUMERO_TRABAJADORES": {
      const antes = state.workers || [];
      const workers = trabajadoresParaNumero(antes, getWorkersForSector(state.sector), action.payload?.n);
      const conCitas = new Set((state.appointments || []).map((a) => a && a.worker_id).filter(Boolean));
      const nuevos = workers.filter((w) => !conCitas.has(idDeTrabajador(w)));
      const extra = nuevos.length
        ? nuevos.flatMap((w) => generateMockCalendarEvents(null, null, state.sector, [w], state.services)
          .map((a) => ({ ...a, event_id: `${a.event_id}-${idDeTrabajador(w)}`, id: `${a.id}-${idDeTrabajador(w)}` })))
        : [];
      return { ...state, workers, appointments: [...(state.appointments || []), ...extra] };
    }

    case "DELETE_WORKER": {
      const uid = action.payload?.user_id;
      if (!uid) return state;
      return {
        ...state,
        workers: (state.workers || []).filter(
          (w) => (w.worker_id || w.user_id || w.id) !== uid,
        ),
      };
    }

    case "UPDATE_CONTACT_SERVICE_CUSTOMIZATIONS": {
      const { contactId, serviceCustomizations } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((c) => {
          const cId = c.contact_id?.S || c.contact_id;
          if (cId === contactId) {
            return {
              ...c,
              serviceCustomizations: {
                M: Object.fromEntries(
                  serviceCustomizations.map((sc) => [
                    sc.serviceId,
                    {
                      M: {
                        serviceId: { S: sc.serviceId },
                        serviceName: { S: sc.serviceName || "" },
                        customDuration: { N: String(sc.customDuration || 0) },
                        standardDuration: { N: String(sc.standardDuration || 0) },
                        isInterleaved: { BOOL: sc.isInterleaved || false },
                        interleavedBlocks: {
                          L: (sc.interleavedBlocks || []).map((block) => ({
                            M: {
                              id: { N: String(block.id) },
                              type: { S: block.type },
                              duration: { N: String(block.duration) },
                              label: { S: block.label || "" },
                            },
                          })),
                        },
                      },
                    },
                  ]),
                ),
              },
            };
          }
          return c;
        }),
      };
    }

    case "NAVIGATE": {
      const payload = action.payload;
      const navSeq = (state.navSeq || 0) + 1;
      if (payload && typeof payload === "object" && payload.page) {
        const options = payload.options || null;
        return { ...state, currentPage: payload.page, navOptions: options, navArrival: options, navSeq };
      }
      return { ...state, currentPage: payload, navOptions: null, navArrival: null, navSeq };
    }

    case "CLEAR_NAV_OPTIONS":
      return { ...state, navOptions: null };

    case "SET_CAMPAIGNS":
      return { ...state, campaigns: action.payload };

    case "SET_SELECTED_CAMPAIGN":
      return { ...state, selectedCampaignId: action.payload };

    case "SET_SELECTED_ALUMNO":
      return { ...state, selectedAlumnoId: action.payload };

    default:
      return state;
  }
}

/* ─── Context y Provider ──────────────────────────────────── */

const SimulatorContext = createContext(null);

export function SimulatorProvider({ children }) {
  const [state, dispatch] = useReducer(simulatorReducer, initialState);

  // Mantener el puente store.js sincronizado en cada render (síncrono, no en efecto).
  // Esto evita race conditions donde los useEffects de páginas hijas llaman
  // axiosClient.get() antes de que el useEffect del provider haya actualizado el puente.
  const stateRef = useRef(state);
  stateRef.current = state;
  linkStore(() => stateRef.current, dispatch);

  const activate = () => {
    activityTracker.startTimer("simulator");
    activityTracker.track("SIMULATOR_OPEN", {
      contact_id: stateRef.current?.contactId || null,
    });
    dispatch({ type: "ACTIVATE" });
  };
  const deactivate = () => {
    try { if (esModoAcademia()) entrarModoAcademiaSim(false); } catch (_) {}
    const duration_ms = activityTracker.endTimer("simulator");
    activityTracker.track("SIMULATOR_CLOSE", {
      sector: stateRef.current?.sector || null,
      contact_id: stateRef.current?.contactId || null,
      duration_ms,
    });
    // Limpiar localStorage del simulador para que la próxima sesión empiece
    // limpia (útil para demos consecutivas: el chooser de lavadero vuelve a
    // preguntar, los servicios sembrados se regeneran, etc.).
    try {
      const KEYS_TO_CLEAR = [
        "sim_lavadero_service_layout",
        "sim_services_lavadero_by_service",
        "sim_service_vehicle_overrides",
        "sim_vehicle_library",
      ];
      for (const k of KEYS_TO_CLEAR) {
        localStorage.removeItem(k);
      }
    } catch (_) {}
    // Autoescuela: fuera los alumnos creados en esta demo y el móvil del lead guardado (sessionStorage);
    // el alumno de prueba de David se queda.
    try { resetSimDemoSession(); } catch (_) {}
    // «Tu agenda llena» (citas): fuera los envíos y avisos de esta demo; el siguiente lead parte de cero.
    try { resetAgendaLlena(); } catch (_) {}
    dispatch({ type: "DEACTIVATE" });
  };
  const setReturnLead = (data) => dispatch({ type: "SET_RETURN_LEAD", payload: data });
  const clearReturnLead = () => dispatch({ type: "CLEAR_RETURN_LEAD" });

  const setSector = (sector, sectorLabel, services, workers, logoUrl, contactId = null, contactName = null, contactPhone = null, personaContactoNombre = null, personaContactoPhone = null) => {
    dispatch({ type: "SET_SECTOR", payload: { sector, sectorLabel, services, workers, logoUrl, contactId, contactName, contactPhone, personaContactoNombre, personaContactoPhone } });
    // Autoescuela: el alumno de prueba pasa a ser EL LEAD (su nombre y su móvil); sin móvil, el de David.
    // Efecto lateral fuera del reducer (la lista de alumnos es un objeto compartido de módulo).
    if (sector === "autoescuela") {
      try { aplicarLeadComoAlumno({ contactName, personaContactoNombre, contactPhone, personaContactoPhone }); } catch (_) {}
      // El negocio es la autoescuela del lead (c_contactos.nombre): vistas previas, chats y lo que llega a su móvil.
      // Sin ficha, el de ejemplo. Los chats de ejemplo se vuelven a armar con ese nombre.
      try { aplicarNombreLeadNegocio(contactName); resetAutoescuelaChats(); } catch (_) {}
    }
  };

  const navigate = (page, options) =>
    dispatch({
      type: "NAVIGATE",
      payload: options ? { page, options } : page,
    });
  const clearNavOptions = () => dispatch({ type: "CLEAR_NAV_OPTIONS" });

  const value = {
    ...state,
    activate,
    deactivate,
    setSector,
    navigate,
    clearNavOptions,
    setReturnLead,
    clearReturnLead,
    dispatch,
  };

  return (
    <SimulatorContext.Provider value={value}>
      {children}
    </SimulatorContext.Provider>
  );
}

export function useSimulator() {
  const context = useContext(SimulatorContext);
  if (!context) {
    throw new Error("useSimulator must be used within SimulatorProvider");
  }
  return context;
}

export default SimulatorContext;
